import {
  useState,
  useRef,
  ChangeEvent,
  KeyboardEvent,
  ClipboardEvent,
  useEffect,
} from "react";

type MultiInputProps = {
  length?: number;
  onChange?: (value: string) => void;
  inputClassName?: string;
  containerClassName?: string;
  error?: string | null;
  defaultValue?: string;
};

export function MultiInput({
  length = 6,
  error,
  onChange,
  inputClassName,
  containerClassName,
  defaultValue = "",
}: MultiInputProps): JSX.Element {
  const [clientIdDigits, setClientIdDigits] = useState<string[]>(
    Array(length).fill("")
  );
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  // Effect to call onChange when clientIdDigits change
  useEffect(() => {
    const clientId = clientIdDigits.join("");
    onChange?.(clientId);
  }, [clientIdDigits, onChange]);

  // Effect to set default value
  useEffect(() => {
    if (defaultValue) {
      const newDigits = defaultValue.split("");
      setClientIdDigits(newDigits);
    }
  }, [defaultValue]);

  const handleChange = (element: HTMLInputElement, index: number): void => {
    if (!/^\d$/.test(element.value) && element.value !== "") return;

    const newDigits = [...clientIdDigits];
    newDigits[index] = element.value;
    setClientIdDigits(newDigits);

    // Move focus to the next input
    if (element.value && index < length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    e: KeyboardEvent<HTMLInputElement>,
    index: number
  ): void => {
    const { key } = e;

    if (key === "Backspace" || key === "Delete") {
      e.preventDefault();
      const newDigits = [...clientIdDigits];
      newDigits[index] = "";
      setClientIdDigits(newDigits);
      if (index > 0) {
        inputsRef.current[index - 1]?.focus();
      }
    } else if (key === "ArrowLeft" && index > 0) {
      inputsRef.current[index - 1]?.focus();
    } else if (key === "ArrowRight" && index < length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLDivElement>): void => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").trim().slice(0, length);
    if (!/^\d+$/.test(pasteData)) return;

    const newDigits = pasteData.split("");
    setClientIdDigits(newDigits);

    // Focus the next empty input or the last one
    const nextIndex = newDigits.length < length ? newDigits.length : length - 1;
    inputsRef.current[nextIndex]?.focus();
  };

  return (
    <div className={containerClassName} onPaste={handlePaste}>
      {clientIdDigits.map((digit, index) => (
        <input
          type="text"
          inputMode="tel"
          name={`clientId-${index}`}
          maxLength={1}
          key={index}
          value={digit}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            handleChange(e.target, index)
          }
          onKeyDown={(e: KeyboardEvent<HTMLInputElement>) =>
            handleKeyDown(e, index)
          }
          ref={(el) => (inputsRef.current[index] = el)}
          className={inputClassName + (error ? " error" : "")}
        />
      ))}
    </div>
  );
}
